import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, errMsg, useApi, useRefresh } from "@/lib/api";
import { PRODUCTS, STAGES, productOf, fmtEur, fmtDate } from "@/lib/crm";
import { PracticeFormDialog } from "@/components/PracticeFormDialog";

const Card = ({ p, onEdit }) => (
  <div draggable onDragStart={(e) => e.dataTransfer.setData("pid", p.practice_id)} data-testid={`kanban-card-${p.practice_id}`}
    className="bg-white rounded-lg border border-slate-200 p-3.5 cursor-grab active:cursor-grabbing hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] border-l-4"
    style={{ borderLeftColor: productOf(p.product).color }} onClick={() => onEdit(p)}>
    <div className="flex justify-between items-start gap-2">
      <Link to={`/clienti/${p.client_id}`} onClick={(e) => e.stopPropagation()} className="font-medium text-sm text-slate-900 hover:text-sky-700">{p.client_name}</Link>
      <span className="text-[10px] uppercase tracking-wider text-slate-400">{productOf(p.product).label}</span>
    </div>
    <p className="font-mono text-base mt-1.5">{fmtEur(p.amount)}</p>
    {p.new_uploads && <p data-testid={`kanban-new-uploads-${p.practice_id}`} className="mt-1 inline-block rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold px-2 py-0.5">Nuovi documenti</p>}
    {p.documents?.length > 0 && <p className="text-[11px] text-slate-400 mt-1">Documenti {p.documents.filter((d) => d.received).length}/{p.documents.length}</p>}
    <p className="text-xs text-slate-500 mt-1 truncate">{p.bank || "—"}{p.deadline ? ` · scad. ${fmtDate(p.deadline)}` : ""}</p>
  </div>
);

export default function Pipeline() {
  const [product, setProduct] = useState("");
  const [edit, setEdit] = useState(null);
  const [over, setOver] = useState(null);
  const refresh = useRefresh();
  const { data = [] } = useApi(`/practices?product=${product}`);

  const drop = async (e, stage) => {
    e.preventDefault();
    setOver(null);
    const pid = e.dataTransfer.getData("pid");
    if (!pid) return;
    try { await api.patch(`/practices/${pid}/stage`, { stage }); refresh(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="pipeline-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Pipeline pratiche</h1>
          <p className="text-slate-500 mt-1">Trascina le pratiche tra le fasi</p>
        </div>
        <button data-testid="add-practice-btn" onClick={() => setEdit({})} className="flex items-center gap-2 rounded-full bg-[#0F172A] text-white px-5 py-2.5 text-sm font-medium hover:bg-sky-600 transition-colors"><Plus className="h-4 w-4" />Nuova pratica</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {[{ id: "", label: "Tutti" }, ...PRODUCTS].map((p) => (
          <button key={p.id || "all"} data-testid={`pipeline-filter-${p.id || "all"}`} onClick={() => setProduct(p.id)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${product === p.id ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{p.label}</button>
        ))}
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {STAGES.map((s) => {
          const items = data.filter((p) => p.stage === s.id);
          const total = items.reduce((a, p) => a + (p.amount || 0), 0);
          return (
            <div key={s.id} data-testid={`kanban-col-${s.id}`} onDragOver={(e) => { e.preventDefault(); if (over !== s.id) setOver(s.id); }} onDragLeave={() => setOver(null)} onDrop={(e) => drop(e, s.id)}
              className={`shrink-0 w-72 rounded-xl border p-3 transition-colors ${over === s.id ? "ring-2 ring-sky-400" : ""} ${s.cls.split(" ").slice(0, 2).join(" ")}`}>
              <div className="flex justify-between items-baseline px-1 mb-3">
                <h3 className={`font-display font-semibold text-sm ${s.cls.split(" ")[2]}`}>{s.label} <span className="opacity-60">({items.length})</span></h3>
                <span className="font-mono text-xs text-slate-500">{fmtEur(total)}</span>
              </div>
              <div className="space-y-2.5 min-h-[120px]">{items.map((p) => <Card key={p.practice_id} p={p} onEdit={setEdit} />)}</div>
            </div>
          );
        })}
      </div>
      <PracticeFormDialog open={!!edit} onOpenChange={() => setEdit(null)} practice={edit?.practice_id ? edit : null} />
    </div>
  );
}
